import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Autocomplete,
} from '@mui/material';
import api from '../api/api';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/es';



const decodeJwtToken = (token) => {
  try {
    const payloadBase64 = token.split('.')[1];
    const decodedPayload = atob(payloadBase64);
    return JSON.parse(decodedPayload);
  } catch (e) {
    console.error("Error al decodificar el token:", e);
    return null;
  }
};

const ProgramarConsulta = ({ visible, onClose, initialDate, id_paciente }) => {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pacientes, setPacientes] = useState([]);
  const [razas, setRazas] = useState([]);
  const [especies, setEspecies] = useState([]); // 🟢 NUEVO ESTADO PARA ESPECIES
  const [sucursales, setSucursales] = useState([]);
  const [selectedPaciente, setSelectedPaciente] = useState(null);
  const [selectedRaza, setSelectedRaza] = useState(null);
  const [selectedSucursal, setSelectedSucursal] = useState(null);
  const [isNewPaciente, setIsNewPaciente] = useState(false);
  const [formData, setFormData] = useState({
  razon_consulta: '',
  fecha_cita: initialDate ? dayjs(initialDate) : dayjs(),
  });
  const [newPacienteData, setNewPacienteData] = useState({
    nombre_paciente: '',
    nombre_cliente: '',
    email_cliente: '',
    telefonos_cliente: '',
    direccion_cliente: '',
    fecha_nac_paciente: '',
    id_raza: '',
  });

  const [idUsuario, setIdUsuario] = useState(null);
  const [idSucursal, setIdSucursal] = useState(null);
  
  // Nuevo estado para la búsqueda con debounce
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingSearch, setLoadingSearch] = useState(false);

  const getFormattedNow = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  useEffect(() => {
  const fetchData = async () => {
    try {
      // 🟢 Modificación para cargar ESPECIES
      const [razasRes, sucursalesRes, especiesRes] = await Promise.all([
        api.get('/razas/'),
        api.get('/sucursales/'),
        api.get('/especies/'), // <--- Asumiendo este endpoint
      ]);
      setRazas(razasRes.data);
      setSucursales(sucursalesRes.data);
      setEspecies(especiesRes.data); // 🟢 Guardar especies
    } catch (err) {
      console.error('Error fetching initial data:', err);
      setError('No se pudieron cargar los datos necesarios.');
    }
  };

  if (visible) {
    setFormData(prev => ({
      ...prev,
      fecha_cita: initialDate ? dayjs(initialDate) : dayjs(),
    }));
    fetchData();
  }
}, [visible, initialDate]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = decodeJwtToken(token);
      if (decodedToken) {
        setIdUsuario(decodedToken.id_usuario);
        setIdSucursal(decodedToken.id_sucursal);
      } else {
        setError("Error de autenticación. Por favor, vuelva a iniciar sesión.");
      }
    }
  }, []);

  // Efecto para precargar datos si hay un ID en los props o la URL
  useEffect(() => {
    const fetchAndSetPatient = async (patientId) => {
      try {
        setLoading(true);
        const response = await api.get(`/pacientes/${patientId}`);
        if (response.data) {
          setSelectedPaciente(response.data);
          setIsNewPaciente(false);
          setError(null);
        } else {
          setError("No se encontró el paciente con el ID proporcionado.");
        }
      } catch (err) {
        console.error('Error fetching patient data:', err);
        setError('Error al cargar los datos del paciente.');
      } finally {
        setLoading(false);
      }
    };

    if (id_paciente) {
      fetchAndSetPatient(id_paciente);
    } else if (id) {
        const fetchClientPatients = async () => {
            try {
                setLoading(true);
                const response = await api.get(`/pacientes/por-cliente/${id}`);
                if (response.data && response.data.length > 0) {
                    setSelectedPaciente(response.data[0]);
                    setIsNewPaciente(false);
                    setError(null);
                } else {
                    setIsNewPaciente(true);
                    setError("No se encontraron pacientes para este cliente. Por favor, crea uno nuevo.");
                }
            } catch (err) {
                console.error('Error fetching patient data for client:', err);
                setError('Error al cargar los pacientes del cliente.');
            } finally {
                setLoading(false);
            }
        };
        fetchClientPatients();
    }
  }, [id, id_paciente]);

  // Efecto para manejar la búsqueda de pacientes con debounce
  useEffect(() => {
    const timerId = setTimeout(async () => {
      if (searchQuery && searchQuery.length >= 3) {
        setLoadingSearch(true);
        try {
          const response = await api.get(`/pacientes/buscar?terminos_busqueda=${searchQuery}`);
          const pacientesConKeys = response.data.map(paciente => ({
            ...paciente,
            key: paciente.id_paciente,
          }));
          setPacientes(pacientesConKeys);
        } catch (err) {
          console.error('Error fetching patients:', err);
          setError('No se pudieron cargar los pacientes.');
          setPacientes([]);
        } finally {
          setLoadingSearch(false);
        }
      } else {
        setPacientes([]);
      }
    }, 500); // Retraso de 500ms
    return () => clearTimeout(timerId);
  }, [searchQuery]);


  // IMPLEMENTACIÓN DEL FILTRO DE RAZAS HABILITADAS
  const filteredRazas = useMemo(() => {
    // Al crear un nuevo paciente, solo permitimos seleccionar razas habilitadas.
    const opciones = razas.filter(r => r.habilitada !== false);
    
    console.log('DEBUG PROGRAMARCONSULTA: Lista filtrada de razas (solo habilitadas):', opciones.length);
    return opciones;
  }, [razas]);


  const handleProgramarConsulta = async (isExpress = false) => {
    if (!formData.razon_consulta || (!selectedPaciente && !isNewPaciente)) {
      setError('Por favor, completa la razón de la consulta y selecciona un paciente.');
      return;
    }
    
    if (!isExpress && !formData.fecha_cita) {
      setError('Por favor, completa la fecha de la cita.');
      return;
    }

    if (!idUsuario || !idSucursal) {
      setError('Error: No se pudo obtener la información de usuario y sucursal del token.');
      return;
    }

    if (isNewPaciente) {
      if (!newPacienteData.nombre_cliente || !newPacienteData.email_cliente || !newPacienteData.nombre_paciente || !newPacienteData.fecha_nac_paciente || !selectedRaza || !selectedSucursal) {
        setError('Por favor, completa los campos de nombre del cliente, email, nombre del paciente, fecha de nacimiento, raza y sucursal.');
        return;
      }
    }

    try {
      setLoading(true);
      let id_paciente_to_use;

      if (isNewPaciente) {
        const formDataPayload = new FormData();
        formDataPayload.append('nombre_cliente', newPacienteData.nombre_cliente);
        formDataPayload.append('email_cliente', newPacienteData.email_cliente);
        formDataPayload.append('telefonos_cliente', newPacienteData.telefonos_cliente);
        formDataPayload.append('direccion_cliente', newPacienteData.direccion_cliente);
        formDataPayload.append('nombre_paciente', newPacienteData.nombre_paciente);
        formDataPayload.append('fecha_nac_paciente', newPacienteData.fecha_nac_paciente);
        formDataPayload.append('id_raza', selectedRaza.id_raza);
        formDataPayload.append('id_sucursal', selectedSucursal.id_sucursal);
        
        const pacienteRes = await api.post('/clientes/cliente-y-paciente', formDataPayload, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        id_paciente_to_use = pacienteRes.data.id_paciente;
      } else {
        id_paciente_to_use = selectedPaciente.id_paciente;
      }

      const citaPayload = {
      id_paciente: id_paciente_to_use,
      razon_consulta: formData.razon_consulta,
      fecha_cita: isExpress 
        ? new Date().toISOString() // Mantenemos .toISOString() para la consulta rápida
        : formData.fecha_cita 
          ? formData.fecha_cita.format('YYYY-MM-DDTHH:mm:ss') // ¡CORREGIDO! Formato "Naive" sin Z.
          : null,
      id_usuario: idUsuario,
      id_sucursal: idSucursal,
      es_express: isExpress,
      es_pendiente: !isExpress
    };

      const response = await api.post('/consultas/programar', citaPayload);
      setError(null);
      alert('Consulta programada exitosamente');
      
      onClose(); // Llama a onClose para cerrar la modal.
      
    } catch (error) {
      if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
        const errorMessages = error.response.data.detail.map(err => {
          return `${err.loc.join(' -> ')}: ${err.msg}`;
        });
        setError(`Error(es) de validación:\n${errorMessages.join('\n')}`);
      } else {
        setError('Error al programar consulta: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!visible) {
    return null;
  }

  return (
  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
    <Box
      sx={{
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        p: 2,
        backgroundColor: '#fff',
      }}
    >
      <Typography variant="h6" gutterBottom>
        Programar Nueva Consulta
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Autocomplete
          options={pacientes}
          getOptionKey={(option) => option.id_paciente}
          getOptionLabel={(option) => `${option.nombre_paciente} - ${option.nombre_cliente}` || ""}
          loading={loadingSearch}
          value={selectedPaciente}
          onChange={(event, newValue) => {
            if (!id_paciente) {
              setSelectedPaciente(newValue);
              setIsNewPaciente(false);
            }
          }}
          onInputChange={(event, newInputValue) => {
            if (!id_paciente) {
              setSearchQuery(newInputValue);
            }
          }}
          readOnly={!!id_paciente}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Buscar o Seleccionar Paciente"
              fullWidth
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loadingSearch ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
        
        {selectedPaciente && (
          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
            Paciente seleccionado: **{selectedPaciente.nombre_paciente}** del cliente **{selectedPaciente.nombre_cliente}**
          </Alert>
        )}

        {!selectedPaciente && (
          <Button onClick={() => setIsNewPaciente(!isNewPaciente)} sx={{ my: 1 }}>
            {isNewPaciente ? 'Ocultar campos de nuevo paciente' : 'O Crear un nuevo paciente'}
          </Button>
        )}

        {isNewPaciente && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle1" sx={{ mt: 2 }}>Datos del Nuevo Paciente y Cliente</Typography>
            <TextField
              label="Nombre del Cliente"
              fullWidth
              value={newPacienteData.nombre_cliente}
              onChange={(e) => setNewPacienteData({ ...newPacienteData, nombre_cliente: e.target.value })}
            />
            <TextField
              label="Email del Cliente"
              fullWidth
              value={newPacienteData.email_cliente}
              onChange={(e) => setNewPacienteData({ ...newPacienteData, email_cliente: e.target.value })}
            />
            <TextField
              label="Teléfonos del Cliente"
              fullWidth
              value={newPacienteData.telefonos_cliente}
              onChange={(e) => setNewPacienteData({ ...newPacienteData, telefonos_cliente: e.target.value })}
            />
            <TextField
              label="Dirección del Cliente"
              fullWidth
              value={newPacienteData.direccion_cliente}
              onChange={(e) => setNewPacienteData({ ...newPacienteData, direccion_cliente: e.target.value })}
            />
            <TextField
              label="Nombre del Paciente (Mascota)"
              fullWidth
              value={newPacienteData.nombre_paciente}
              onChange={(e) => setNewPacienteData({ ...newPacienteData, nombre_paciente: e.target.value })}
            />
            <Autocomplete
              options={sucursales}
              getOptionKey={(option) => option.id_sucursal}
              getOptionLabel={(option) => option.nombre_sucursal || ""}
              onChange={(event, newValue) => {
                setSelectedSucursal(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Seleccionar Sucursal"
                  fullWidth
                  variant="outlined"
                />
              )}
            />
            <Autocomplete
              options={filteredRazas} 
              getOptionKey={(option) => option.id_raza}
              // 🟢 LÓGICA DE CONCATENACIÓN AÑADIDA
              getOptionLabel={(option) => {
                // 1. Encontrar la especie
                const especie = especies.find(e => e.id_especie === option.id_especie);
                
                // 2. Obtener el nombre
                const nombreEspecie = especie ? especie.nombre_especie : 'N/A Especie';

                // 3. Traza de depuración
                console.log(`DEBUG PROGRAMARCONSULTA RAZA: Raza ID ${option.id_raza}, Especie: ${nombreEspecie}, Habilitada: ${option.habilitada}`, option);
                
                // 4. Etiqueta concatenada: "Raza - Especie"
                return `${option.nombre_raza || 'N/A Raza'} - ${nombreEspecie}`;
              }}
              onChange={(event, newValue) => {
                setSelectedRaza(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Seleccionar Raza"
                  fullWidth
                  variant="outlined"
                />
              )}
            />
            <TextField
              label="Fecha de Nacimiento Paciente"
              type="date"
              fullWidth
              value={newPacienteData.fecha_nac_paciente}
              onChange={(e) => setNewPacienteData({ ...newPacienteData, fecha_nac_paciente: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        )}
        
        <TextField
          label="Razón de Consulta"
          fullWidth
          value={formData.razon_consulta}
          onChange={(e) => setFormData({ ...formData, razon_consulta: e.target.value })}
          sx={{ mt: 2 }}
        />
        
        <DateTimePicker
        label="Fecha de Cita"
        value={formData.fecha_cita}
        onChange={(newValue) => setFormData({ ...formData, fecha_cita: newValue })}
        format="DD/MM/YYYY hh:mm A"
        ampm={true}   ///para el reloj am y pm
        minutesStep={30}
        slotProps={{ 
            textField: { fullWidth: true },
            timeClock: { 
              minutesStep: 30, // <--- AÑADIDO: La forma recomendada por  limitamos 00 y 30 para ñlas citas
            },
          }}
      />
        
        {error && <Alert severity="error" sx={{ whiteSpace: 'pre-wrap' }}>{error}</Alert>}
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
          <Button onClick={onClose} variant="outlined">
            Cancelar
          </Button>

          {selectedPaciente && (
            <Button
              onClick={() => handleProgramarConsulta(true)}
              variant="contained"
              disabled={loading || !formData.razon_consulta}
              sx={{ backgroundColor: 'orange', '&:hover': { backgroundColor: 'darkorange' } }}
            >
              {loading ? <CircularProgress size={24} /> : 'Consulta Rápida'}
            </Button>
          )}

          <Button
            onClick={() => handleProgramarConsulta()}
            variant="contained"
            disabled={loading || (!selectedPaciente && !isNewPaciente) || !formData.fecha_cita}
          >
            {loading ? <CircularProgress size={24} /> : 'Programar Cita'}
          </Button>
        </Box>
      </Box>
    </Box>
      </LocalizationProvider>
  );
};

export default ProgramarConsulta;